import type { IchimokuPoint, IchimokuStyle, OscillatorStyle, ZigZagPoint } from "../types/indicator-types";
import { colorWithOpacity } from "../types/indicator-types";

type DrawOptions = {
  ctx: CanvasRenderingContext2D;
  ichimoku?: IchimokuPoint[];
  zigzag?: ZigZagPoint[];
  ichimokuStyle?: IchimokuStyle;
  zigzagStyle?: OscillatorStyle;
  viewportStartTime: number;
  viewportEndTime: number;
  windowSize: number;
  drawStartX: number;
  drawWidth: number;
  getY: (value: number) => number;
};

const ICHIMOKU_LINES = [
  ["tenkan", "conversion"],
  ["kijun", "base"],
  ["senkouA", "spanA"],
  ["senkouB", "spanB"],
  ["chikou", "lagging"],
] as const;

export function drawAdvancedOverlays({
  ctx,
  ichimoku,
  zigzag,
  ichimokuStyle,
  zigzagStyle,
  viewportStartTime,
  viewportEndTime,
  windowSize,
  drawStartX,
  drawWidth,
  getY,
}: DrawOptions) {
  const getX = (time: number) => drawStartX + ((time - viewportStartTime) / windowSize) * drawWidth;
  const inRange = (time: number) =>
    time >= viewportStartTime - windowSize * 0.15 && time <= viewportEndTime + windowSize * 0.15;

  if (ichimoku && ichimoku.length > 0) {
    const visible = ichimoku.filter((p) => inRange(p.time));
    const cloud = visible.filter(
      (p): p is IchimokuPoint & { senkouA: number; senkouB: number } =>
        Number.isFinite(p.senkouA) && Number.isFinite(p.senkouB),
    );

    // One trapezoid per interval allows the cloud colour to change cleanly at a crossover.
    for (let i = 1; i < cloud.length; i++) {
      const a = cloud[i - 1]!;
      const b = cloud[i]!;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(getX(a.time), getY(a.senkouA));
      ctx.lineTo(getX(b.time), getY(b.senkouA));
      ctx.lineTo(getX(b.time), getY(b.senkouB));
      ctx.lineTo(getX(a.time), getY(a.senkouB));
      ctx.closePath();
      const bullish = (a.senkouA + b.senkouA) / 2 >= (a.senkouB + b.senkouB) / 2;
      const cloudStyle = bullish ? ichimokuStyle?.bullishCloud : ichimokuStyle?.bearishCloud;
      if (cloudStyle?.visible !== false) {
        ctx.fillStyle = colorWithOpacity(
          cloudStyle?.color ?? (bullish ? "#86DA0E" : "#FF0000"),
          cloudStyle?.opacity ?? 0.13,
        );
        ctx.fill();
      }
      ctx.restore();
    }

    for (const [key, styleKey] of ICHIMOKU_LINES) {
      const lineStyle = ichimokuStyle?.[styleKey];
      if (lineStyle?.visible === false) continue;
      const points = visible.filter((p) => Number.isFinite(p[key]));
      if (points.length < 2) continue;
      ctx.save();
      ctx.strokeStyle =
        lineStyle?.color ??
        ({ tenkan: "#2256FF", kijun: "#FF0000", senkouA: "#86DA0E", senkouB: "#FF00FF", chikou: "#C0C0C0" } as const)[
          key
        ];
      ctx.lineWidth = lineStyle?.width ?? 1;
      ctx.globalAlpha = lineStyle?.opacity ?? (key === "chikou" ? 0.75 : 1);
      ctx.lineJoin = "round";
      ctx.beginPath();
      points.forEach((p, index) => {
        const x = getX(p.time);
        const y = getY(p[key]!);
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.restore();
    }
  }

  if (zigzag && zigzag.length >= 2) {
    const visibleSegments = zigzag.slice(1).flatMap((current, index) => {
      const previous = zigzag[index]!;
      if (current.startsSegment) return [];
      return inRange(previous.time) ||
        inRange(current.time) ||
        (previous.time < viewportStartTime && current.time > viewportEndTime)
        ? [{ previous, current }]
        : [];
    });
    if (visibleSegments.length > 0) {
      ctx.save();
      ctx.lineWidth = 1;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      for (const { previous, current } of visibleSegments) {
        const lineStyle = zigzagStyle?.main;
        if (lineStyle?.visible === false) continue;
        ctx.strokeStyle = lineStyle?.color ?? "#E67E22";
        ctx.lineWidth = lineStyle?.width ?? 1;
        ctx.globalAlpha = lineStyle?.opacity ?? 1;
        ctx.beginPath();
        ctx.moveTo(getX(previous.time), getY(previous.value));
        ctx.lineTo(getX(current.time), getY(current.value));
        ctx.stroke();
      }
      ctx.restore();
    }
  }
}
